import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import saleService from "../../../Services/saleService";

const Income = () => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  const [searchData, setSearchData] = useState("");
  const [medicinesGroupedData, setMedicinesGroupedData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [servicesGroupedData, setServicesGroupedData] = useState([]);
  const [patientGroupedData, setPatientGroupedData] = useState([]);
  const [pharmacyGroupedData, setPharmacyPatientGroupedData] = useState([]);
  const [paginatedMedicinesData, setPaginatedMedicinesData] = useState([]);
  const [paginatedServicesData, setPaginatedServicesData] = useState([]);
  const [paginatedPatientData, setPaginatedPatientData] = useState([]);
  const [paginatedPharmacyData, setPaginatedPharmacyData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const navigate = useNavigate();

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        setLoading(true);
        const response = await saleService.fetchAllMedicinesSales();
        const grouped = groupByDate(response.sales || []);
        setMedicinesGroupedData(grouped);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        toast.error("Error fetching medicines sales data");
      }
    };
    fetchSalesData();
  }, []);

  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        setLoading(true);
        const response = await saleService.fetchAllServicesSale();
        const grouped = groupByDate(response.sales || []);
        setServicesGroupedData(grouped);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        toast.error("Error fetching services sales data");
      }
    };
    fetchSalesData();
  }, []);

  useEffect(() => {
    const fetchPatientSalesData = async () => {
      try {
        setLoading(true);
        const response = await saleService.fetchAllPSales();
        const grouped = groupByPatientDate(response.sales || []);
        setPatientGroupedData(grouped);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        toast.error("Error fetching patient sales data");
      }
    };
    fetchPatientSalesData();
  }, []);

  useEffect(() => {
    const fetchPharmacySales = async () => {
      try {
        setLoading(true);
        const response = await saleService.fetchAllPharmacySales();
        const grouped = groupByPharmacyDate(response.sales || []);
        setPharmacyPatientGroupedData(grouped);
        const totalAmount = grouped
          .reduce((sum, item) => sum + parseFloat(item.totalAmount), 0)
          .toFixed(2);
        setTotalAmount(totalAmount);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        toast.error("Error fetching pharmacy sales data");
      }
    };
    fetchPharmacySales();
  }, []);

  useEffect(() => {
    const filteredMedicinesResult =
      medicinesGroupedData?.filter((item) =>
        (item.invoice || "").toLowerCase().includes(searchData.toLowerCase())
      ) || [];
    const filteredServicesResult =
      servicesGroupedData?.filter((item) =>
        (item.invoice || "").toLowerCase().includes(searchData.toLowerCase())
      ) || [];
    const filteredPatientResult =
      patientGroupedData?.filter((item) =>
        (item.patient_id || "").toLowerCase().includes(searchData.toLowerCase())
      ) || [];
    const filteredPharmacyResult =
      pharmacyGroupedData?.filter((item) =>
        (item.id || "").toLowerCase().includes(searchData.toLowerCase())
      ) || [];
    setCurrentPage(1);
    setPaginatedMedicinesData(filteredMedicinesResult);
    setPaginatedServicesData(filteredServicesResult);
    setPaginatedPatientData(filteredPatientResult);
    setPaginatedPharmacyData(filteredPharmacyResult);
  }, [
    searchData,
    medicinesGroupedData,
    servicesGroupedData,
    patientGroupedData,
    pharmacyGroupedData,
  ]);

  const groupByDate = (sales) => {
    const dateMap = {};
    const allDates = getAllDatesInRange();

    sales.forEach((sale) => {
      const { invoice, unit_price, quantity, sale_date } = sale;
      const total = quantity * unit_price;
      const dateKey = formatDate(sale_date);

      if (!dateMap[dateKey]) {
        dateMap[dateKey] = {
          date: dateKey,
          totalAmount: 0,
          invoices: new Set(),
        };
      }

      dateMap[dateKey].totalAmount += total;
      dateMap[dateKey].invoices.add(invoice);
    });

    allDates.forEach((date) => {
      if (!dateMap[date]) {
        dateMap[date] = {
          date,
          totalAmount: 0,
          invoices: new Set(),
        };
      }
    });

    const sortedData = Object.values(dateMap)
      .map((group) => ({
        ...group,
        totalInvoiceCount: group.invoices.size,
      }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));

    return sortedData;
  };

  const groupByPatientDate = (sales) => {
    const dateMap = {};
    const allDates = getAllDatesInRange();

    sales.forEach((sale) => {
      const { patient_id, fee, date } = sale;
      const totalAmount = fee;
      const dateKey = formatDate(date);

      if (!dateMap[dateKey]) {
        dateMap[dateKey] = {
          date: dateKey,
          totalAmount: 0,
          patientCount: 0,
        };
      }

      dateMap[dateKey].totalAmount += totalAmount;
      dateMap[dateKey].patientCount += 1;
    });

    allDates.forEach((date) => {
      if (!dateMap[date]) {
        dateMap[date] = {
          date,
          totalAmount: 0,
          patientCount: 0,
        };
      }
    });

    const sortedData = Object.values(dateMap)
      .map((group) => ({
        ...group,
      }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));

    return sortedData;
  };

  const groupByPharmacyDate = (sales) => {
    const dateMap = {};
    const allDates = getAllDatesInRange();

    sales.forEach((sale) => {
      const { amount, date } = sale;
      const totalAmount = parseFloat(amount);
      const dateKey = formatDate(date);

      if (!dateMap[dateKey]) {
        dateMap[dateKey] = {
          date: dateKey,
          totalAmount: 0,
          pharmacyCount: 0,
        };
      }

      dateMap[dateKey].totalAmount += totalAmount;
      dateMap[dateKey].pharmacyCount += 1;
    });

    allDates.forEach((date) => {
      if (!dateMap[date]) {
        dateMap[date] = {
          date,
          totalAmount: 0,
          pharmacyCount: 0,
        };
      }
    });

    return Object.values(dateMap)
      .map((group) => ({
        ...group,
        totalAmount: group.totalAmount.toFixed(2),
      }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const getAllDatesInRange = () => {
    const today = new Date();
    const allDates = [];

    for (let i = 0; i <= 31; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      allDates.push(formatDate(date));
    }

    return allDates;
  };

  const formatDate = (date) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return date instanceof Date
      ? date.toLocaleDateString("en-US", options)
      : new Date(date).toLocaleDateString("en-US", options);
  };

  const filterDataByMonth = (data) => {
    return data.filter(
      (item) =>
        new Date(item.date).getMonth() + 1 === Number(selectedMonth) &&
        new Date(item.date).getFullYear() === Number(selectedYear)
    );
  };

  const calculateTotal = (data, key) => {
    return data.reduce((sum, item) => sum + parseFloat(item[key] || 0), 0);
  };

  const combinedData = filterDataByMonth(paginatedServicesData).map(
    (serviceGroup) => {
      const medicinesData =
        paginatedMedicinesData.find((m) => m.date === serviceGroup.date) || {};
      const patientData =
        paginatedPatientData.find((p) => p.date === serviceGroup.date) || {};
      const pharmacyData =
        paginatedPharmacyData.find((ph) => ph.date === serviceGroup.date) || {};

      return {
        date: serviceGroup.date,
        medicinesSales: medicinesData.totalInvoiceCount || 0,
        medicinesAmount: medicinesData.totalAmount || 0,
        servicesSales: serviceGroup.totalInvoiceCount || 0,
        servicesAmount: serviceGroup.totalAmount || 0,
        patientSales: patientData.patientCount || 0,
        patientAmount: patientData.totalAmount || 0,
      };
    }
  );

  const categories = [
    {
      key: "patient",
      label: "Patient",
      salesKey: "patientSales",
      amountKey: "patientAmount",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      color: "bg-blue-50 text-blue-600",
      dot: "bg-blue-400",
    },
    {
      key: "medicines",
      label: "Medicines",
      salesKey: "medicinesSales",
      amountKey: "medicinesAmount",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      ),
      color: "bg-emerald-50 text-emerald-600",
      dot: "bg-emerald-400",
    },
    {
      key: "services",
      label: "Services",
      salesKey: "servicesSales",
      amountKey: "servicesAmount",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      color: "bg-violet-50 text-violet-600",
      dot: "bg-violet-400",
    },
  ];
const grandTotal = combinedData.reduce(
  (sum, item) =>
    sum +
    item.medicinesAmount +
    item.servicesAmount +
    item.patientAmount +
    parseFloat(item.pharmacyAmount || 0),
  0
);
  return (
    <div className="p-2">
      <div className="p-3 sm:p-6">

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white border border-gray-200 rounded-xl px-4 sm:px-6 py-4 mb-5 shadow-sm gap-3">
          <div>
            <h1 className="text-[17px] font-[500] text-gray-800">Income Summary</h1>
            <p className="text-[12px] text-gray-400 mt-0.5">
              {combinedData.length} records found
            </p>
          </div>
          <div className="flex gap-2 sm:gap-3">
            <select
              className="flex-1 sm:flex-none text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-[7px] focus:outline-none text-gray-700"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {months.map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>
            <select
              className="flex-1 sm:flex-none text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-[7px] focus:outline-none text-gray-700"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <option value={2024}>2024</option>
              <option value={2025}>2025</option>
              <option value={2026}>2026</option>
            </select>
          </div>
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full border-collapse shadow-md rounded-lg overflow-hidden">
            <thead>
               <tr className="bg-[#2F6DB4] text-[11px] uppercase text-white text-center">
                <th className="py-3 px-4 text-left" rowSpan={2}>Date</th>
                <th className="py-2 px-4 border-l border-gray-600" colSpan={2}>Patient</th>
                <th className="py-2 px-4 border-l border-gray-600" colSpan={2}>Medicines</th>
                <th className="py-2 px-4 border-l border-gray-600" colSpan={2}>Services</th>
                <th className="py-2 px-4 border-l border-gray-600" rowSpan={2}>Total Amount</th>
              </tr>
                <tr className="bg-[#5092dd] text-[11px] uppercase text-gray-100 text-center">
                <th className="py-2 px-4 border-l border-gray-600">Sales</th>
                <th className="py-2 px-4">Amount</th>
                <th className="py-2 px-4 border-l border-gray-600">Sales</th>
                <th className="py-2 px-4">Amount</th>
                <th className="py-2 px-4 border-l border-gray-600">Sales</th>
                <th className="py-2 px-4">Amount</th>
              </tr>
            </thead>
            <tbody>
              {combinedData.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-gray-400 text-sm bg-white">
                    No records found for this period.
                  </td>
                </tr>
              ) : (
                combinedData.map((item, index) => (
                  <tr
                    key={item.date}
                    className={`border-t text-[13px] hover:bg-gray-50 ${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <td className="py-2 px-4 font-[500] text-gray-800 whitespace-nowrap">{item.date}</td>
                    <td className="py-2 px-4 text-center">{item.patientSales}</td>
                    <td className="py-2 px-4 text-right">{item.patientAmount}</td>
                    <td className="py-2 px-4 text-center">{item.medicinesSales}</td>
                    <td className="py-2 px-4 text-right">{item.medicinesAmount}</td>
                    <td className="py-2 px-4 text-center">{item.servicesSales}</td>
                    <td className="py-2 px-4 text-right">{item.servicesAmount}</td>
                    <td className="py-2 px-4 text-right font-semibold text-gray-800">
                      {(
                        item.medicinesAmount +
                        item.servicesAmount +
                        item.patientAmount +
                        parseFloat(item.pharmacyAmount || 0)
                      ).toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="md:hidden flex flex-col gap-3">
          {combinedData.map((item) => {
            const rowTotal = (
              item.medicinesAmount +
              item.servicesAmount +
              item.patientAmount +
              parseFloat(item.pharmacyAmount || 0)
            ).toFixed(2);

            return (
              <div
                key={item.date}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm"
              >
                <div className="bg-[#2F6DB4] text-white text-[13px] font-medium px-4 py-2">
                  {item.date}
                </div>

                <div className="grid grid-cols-3 divide-x divide-gray-100">
                  <div className="px-3 py-3">
                    <p className="text-[10px] uppercase text-gray-400 mb-1">Patient</p>
                    <p className="text-[13px] font-medium text-gray-800">{item.patientSales} sales</p>
                    <p className="text-[12px] text-gray-500 mt-0.5">{item.patientAmount}</p>
                  </div>
                  <div className="px-3 py-3">
                    <p className="text-[10px] uppercase text-gray-400 mb-1">Medicines</p>
                    <p className="text-[13px] font-medium text-gray-800">{item.medicinesSales} sales</p>
                    <p className="text-[12px] text-gray-500 mt-0.5">{item.medicinesAmount}</p>
                  </div>
                  <div className="px-3 py-3">
                    <p className="text-[10px] uppercase text-gray-400 mb-1">Services</p>
                    <p className="text-[13px] font-medium text-gray-800">{item.servicesSales} sales</p>
                    <p className="text-[12px] text-gray-500 mt-0.5">{item.servicesAmount}</p>
                  </div>
                </div>

                <div className="bg-gray-50 border-t border-gray-100 px-4 py-2 flex justify-between items-center">
                  <span className="text-[11px] uppercase text-gray-400">Total amount</span>
                  <span className="text-[14px] font-semibold text-[#2F6DB4]">{rowTotal}</span>
                </div>
              </div>
            );
          })}

          {combinedData.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex justify-between items-center">
              <span className="text-[12px] uppercase text-blue-500 font-medium">Grand total</span>
              <span className="text-[15px] font-bold text-[#2F6DB4]">{grandTotal.toFixed(2)}</span>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Income;